import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { MotorCarrierSearchCriteria } from '../models/motor-carrier-search-criteria.model';
import { MotorCarrier } from '../models/motor-carrier.model';
import { MotorCarrierAccountData } from '../models/motor-carrier-account/motor-carrier-account-data';
import { MotorCarrierAccountAdditionalContact } from '../models/motor-carrier-account/motor-carrier-account-additional-contact';
import { MotorCarrierAccountOfficer } from '../models/motor-carrier-account/motor-carrier-account-company-officer';
import { MotorCarrierAccountNumber } from '../models/motor-carrier-account/motor-carrier-account-number';
import { MotorCarrierAccountAddress } from '../models/motor-carrier-account/motor-carrier-account-address';
import { CustomerAddress } from '../../customer/models/customer-address.model';
import { MotorCarrierAccountExchangeRate } from '../models/exchange-rate/motor-carrier-account-exchange-rate';
import { MotorCarrierProcessAgents } from '../models/motor-carrier-process-agents/motor-carrier-process-agents';
import { MotorCarrierCorrespondenceData } from '../models/motor-carrier-account/motor-carrier-correspondence-data';
import { MvdNowAspNetRoleModel } from '../../business-suite/shared/models/mvdnowaspnetrole.model';
import { MotorCarrierAccountChangeName } from '../models/business-transaction/motor-carrier-account-change-name.model';

@Injectable({
    providedIn: 'root'
})
export class MotorCarrierService extends BaseApiService {
    constructor() {
        super();
    }

    public legacySearchMotorCarriers(criteria: MotorCarrierSearchCriteria): Observable<MotorCarrier[]> {
        return this.post('IRP/MotorCarrier/Legacy/Search', criteria).pipe(map(r => r.result));
    }

    public searchMotorCarriers(criteria: MotorCarrierSearchCriteria): Observable<MotorCarrier[]> {
        return this.post('IRP/MotorCarrier/Search/Summary', criteria).pipe(map(r => r.result));
    }

    /**
     * Gets the motor carrier account summary data
     * @param motorCarrierAccountNumberId The motor carrier account number id
     */
    public getMotorCarrierAccountSummary(motorCarrierAccountNumberId: number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Summary`);
    }

    public CreateMotorCarrierAccount(criteria: MotorCarrierAccountData): Observable<any> {
        return this.post('IRP/MotorCarrier/Create/Account', criteria).pipe(map(r => r));
    }

    public validateMotorCarrierAccountAddress(customerNumberId: number): Observable<any> {
        return this.get(`Address/Address/Customer/${customerNumberId}`);
    }

    public getMotorCarrierDetails(motorCarrierAccountNumberId: number) {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Details`);
    }

    public SaveMotorCarrierAdditionalContact(criteria: MotorCarrierAccountAdditionalContact): Observable<any> {
        return this.post('IRP/MotorCarrier/AdditionalContact', criteria).pipe(map(r => r.result));
    }

    public SaveMotorCarrierAccountAddress(criteria: MotorCarrierAccountAddress): Observable<any> {
        return this.post('IRP/MotorCarrier/CarrierMailingAddress', criteria).pipe(map(r => r.result));
    }

    public UpdateEstablishedPlaceOfBusiness(criteria: MotorCarrierAccountAddress): Observable<any> {
        return this.post('IRP/MotorCarrier/Account/EstablishedPlaceOfBusiness', criteria).pipe(map(r => r));
    }

    public SaveMotorCarrierOfficer(criteria: MotorCarrierAccountOfficer): Observable<any> {
        return this.post('IRP/MotorCarrier/CompanyOfficer', criteria).pipe(map(r => r.result));
    }
    public SaveMotorCarrierAccountNumber(criteria: MotorCarrierAccountNumber): Observable<any> {
        return this.post('IRP/MotorCarrier/AccountNumber', criteria).pipe(map(r => r.result));
    }

    public saveMotorCarrierAccountFleetWeightGroup(criteria: any): Observable<any> {
        return this.post('IRP/MotorCarrierWeightGroup/Account/Fleet/WeightGroup', criteria).pipe(map(r => r));
    }

    public getFleetWeightGroupNumber(motorCarrierAccountFleetId: number): Observable<any> {
        return this.get('IRP/MotorCarrierWeightGroup/Fleet/' + motorCarrierAccountFleetId);
    }

    public getFleetWeightsOfJurisdictions(workGroupNumber: number, accountFleetId: number): Observable<any> {
        return this.get('IRP/MotorCarrierWeightGroup/Jurisdictions/' + workGroupNumber + '/fleet/' + accountFleetId);
    }

    public getAZWeights(): Observable<any> {
        return this.get('IRP/MotorCarrierWeightGroup/AZWeights/');
    }

    public getJurisdictionMaxandMinWeights(): Observable<any> {
        return this.get('IRP/MotorCarrierWeightGroup/Jurisdiction/MaxMinWeights/');
    }

    public getMotorCarrierAccountIRP(motorCarrierAccountNumberId: number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/IRP`);
    }

    public CheckCustomerIsDeceased(motorCarrierAccountNumberId: number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Status`);
    }

    public saveMotorCarrierAccountIRPStatus = (motorCarrierAccountNumberId: number, motorCarrierIRPStatusTypeId: number) => this.post('IRP/MotorCarrier/Account/' + motorCarrierAccountNumberId + '/IRP/Status/' + motorCarrierIRPStatusTypeId, null);

    public getJurisdictionMileage(): Observable<any> {
        return this.get('IRP/MotorCarrierMileage/Jurisdiction/Mileage');
    }

    public getJurisdictionMileageForAuditedFleet(motorCarrierAccountFleetId: number): Observable<any> {
        return this.get(`IRP/MotorCarrierMileage/Jurisdiction/Mileage/${motorCarrierAccountFleetId}`);
    }

    public saveMotorCarrierAccountFleetMileage(criteria: any): Observable<any> {
        return this.post('IRP/MotorCarrierMileage/Account/Fleet/Mileage', criteria).pipe(map(r => r));
    }

    public getFleetMileagesOfJurisdictions(motorCarrierAccountFleetId: number, motorCarrierAccountNumberId: number): Observable<any> {
        return this.get(`IRP/MotorCarrierMileage/Account/${motorCarrierAccountNumberId}/Jurisdictions/Fleet/${motorCarrierAccountFleetId}`);
    }

    public getFleetRenewalMileagesOfJurisdictions(motorCarrierAccountFleetId: number, motorCarrierAccountNumberId: number): Observable<any> {
        return this.get(`IRP/MotorCarrierMileage/Account/${motorCarrierAccountNumberId}/Jurisdictions/Renewal/Fleet/${motorCarrierAccountFleetId}`);
    }

    public deleteCompanyOfficer(motorCarrierAccountNumberId: number, motorCarrierAccountCompanyOfficerId: number) {
        return this.delete(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Officer/${motorCarrierAccountCompanyOfficerId}`);
    }

    public deleteAdditionalContact(motorCarrierAccountNumberId: number, motorCarrierAccountAdditionalContactId: number) {
        return this.delete(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Delete/${motorCarrierAccountAdditionalContactId}`);
    }

    public deleteMotorCarrierMailingAddress(motorCarrierAccountNumberId: number, customerAddressId: number) {
        return this.delete(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Address/${customerAddressId}`);
    }

    public getMotorCarrierSummaryByCustomerNumberId(customerNumberId: number) {
        return this.get(`IRP/MotorCarrier/Account/${customerNumberId}`);
    }

    public getMotorCarrierAccountSupplements(motorCarrierAccountNumberId: number, motorCarrierAccountSupplementStatusTypeId: number, year: Number, fleetNumber: Number, programId: Number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Supplements/Status/${motorCarrierAccountSupplementStatusTypeId}/Year/${year}/Fleet/${fleetNumber}/program/${programId}`);
    }

    public generateCustomerStatement(motorcarrierAccountNumberId: number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/${motorcarrierAccountNumberId}/Supplements/GenerateStatement`).pipe(map(r => r));
    }

    public getMotorCarrierConfiguration() {
        return this.get('IRP/Configuration/ConfigurationData');
    }

    public getCustomerAddressHistoryByCustomerAddressId(customerAddressId: number) {
        return this.get(`IRP/MotorCarrier/Account/Address/${customerAddressId}/History`);
    }

    public CheckDuplicateMotorCarrierAccountPhysicalAddress(criteria: CustomerAddress[]): Observable<any> {
        return this.post('IRP/MotorCarrier/Account/CheckDuplicateAddress', criteria).pipe(map(r => r));
    }

    public ValidateMotorCarrierAccountData(criteria: MotorCarrierAccountData): Observable<any> {
        return this.post('IRP/MotorCarrier/Account/ValidateMotorCarrierAccountData', criteria).pipe(map(r => r));
    }

    public getValidateUSDOTNumber(usdotNumber: number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/USDOTNumber/${usdotNumber}`);
    }

    public getUSDOTClassificationInformation(usdotNumber: number): Observable<{ result: any[] }> {
        return this.get(`IRP/MotorCarrier/Account/USDOTNumber/${usdotNumber}/Classification`);
    }

    public sendCorrespondenceEmail(criteria: MotorCarrierCorrespondenceData): Observable<any> {
        return this.post('IRP/MotorCarrier/Account/SendCorrespondenceEmail', criteria).pipe(map(r => r));
    }

    public getMotorCarrierAccountUSDOTHistory(motorCarrierAccountNumberId: number) {
        return this.get(`IRP/MotorCarrier/Account/USDOT/${motorCarrierAccountNumberId}/History`);
    }

    public checkDuplicateMotorCarrierAccount(customerNumberId: number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/CheckDuplicate/Customer/${customerNumberId}`);
    }

    public getMotorCarrierAccountGetClosedOrCompletedSupplements(motorCarrierAccountNumberId: number, year: Number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Supplements/ClosedOrCompleteStatus/Year/${year}`);
    }

    public getMotorCarrierCanadianExchangeRates(): Observable<any> {
        return this.get('IRP/CanadianExchangeRate');
    }

    public addMotorCarrierCanadianExchangeRate(criteria: MotorCarrierAccountExchangeRate): Observable<any> {
        return this.post('IRP/CanadianExchangeRate', criteria).pipe(map(r => r.result));
    }

    public deleteMotorCarrierCanadianExchangeRate(canadianExchangeRateId: number) {
        return this.delete(`IRP/CanadianExchangeRate/${canadianExchangeRateId}`);
    }

    public getMotorCarrierAccountAgents(motorCarrierAccountNumberId: number) {
        return this.get(`IRP/ProcessAgent/MotorCarrierAccount/${motorCarrierAccountNumberId}`);
    }

    public addMotorCarrierAccountAgents(criteria: MotorCarrierProcessAgents): Observable<MotorCarrierProcessAgents> {
        return this.post('IRP/ProcessAgent', criteria).pipe(map(r => r.result));
    }

    public removeMotorCarrierAccountAgent(deleteAgentModel: any) {
        return this.post('IRP/ProcessAgent/Unlink', deleteAgentModel);
    }

    public updateMotorCarrierProcessAgentRoles(processAgent: MotorCarrierProcessAgents): Observable<boolean> {
        return this.put('IRP/ProcessAgent', processAgent);
    }

    public getMotorCarrierProcessAgentUserRoles(): Observable<MvdNowAspNetRoleModel[]> {
        return this.get('IRP/ProcessAgent/UserRoles').pipe(map(r => r.result));
    }

    public getMotorCarrierAccountOOSDetails(motorCarrierAccountNumberId: number) {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/OOSDetails`);
    }

    public getMotorCarrierAlertMessagess(motorCarrierAccountNumberId: number) {
        return this.get(`IRP/MotorCarrier/Account/${motorCarrierAccountNumberId}/Alerts`);
    }

    public trackCarrierOOSDetails(model: any) {
        return this.post('IRP/MotorCarrier/Account/TrackOOSDenials', model);
    }

    public getValidateFleetTypeAndCommodityClassTypes(usdotNumber: number, motorCarrierFleetTypeId: number, motorCarrierCommodityClassTypeId: number): Observable<any> {
        return this.get(`IRP/MotorCarrier/Account/USDOT/${usdotNumber}/FleetType/${motorCarrierFleetTypeId}/CommodityClassType/${motorCarrierCommodityClassTypeId}/Validation`);
    }

    public ValidateMotorCarrierEditAccountData(criteria: MotorCarrierAccountChangeName): Observable<any> {
        return this.post('IRP/MotorCarrier/Account/ValidateMotorCarrierAccountEditData', criteria).pipe(map(r => r));
    }

    public getMotorCarrierDeleteVehicleCredits(fleetId: number): Observable<any> {
        return this.get(`IRP/Transaction/${fleetId}/Credits`);
    }
}
